import React, { useState, useEffect } from "react";
import {
	Box,
	Button,
	Container,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	TextField,
	Grid,
	Card,
	CardContent,
	CircularProgress,
	Alert,
	Snackbar,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Chip,
	Stack,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from "@mui/material";
import {
	Add as AddIcon,
	Edit as EditIcon,
	Save as SaveIcon,
	Cancel as CancelIcon,
	ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";

const ReportSettings = () => {
	const [settings, setSettings] = useState([]);
	const [grades, setGrades] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingSetting, setEditingSetting] = useState(null);
	const [formData, setFormData] = useState({
		month: "",
		year: "",
		parent_grade: "",
		number_of_videos_to_be_completed: 4,
		description: "",
	});
	const { user } = useAuth();

	const months = [
		{ value: 1, label: "January" },
		{ value: 2, label: "February" },
		{ value: 3, label: "March" },
		{ value: 4, label: "April" },
		{ value: 5, label: "May" },
		{ value: 6, label: "June" },
		{ value: 7, label: "July" },
		{ value: 8, label: "August" },
		{ value: 9, label: "September" },
		{ value: 10, label: "October" },
		{ value: 11, label: "November" },
		{ value: 12, label: "December" },
	];

	useEffect(() => {
		fetchSettings();
		fetchGrades();
	}, []);

	const fetchSettings = async () => {
		setLoading(true);
		try {
			const { data, error } = await supabase
				.from("report_settings")
				.select("*")
				.eq("status", true)
				.order("year", { ascending: false })
				.order("month", { ascending: true })
				.order("parent_grade", { ascending: true });

			if (error) throw error;
			setSettings(data || []);
		} catch (error) {
			console.error("Error fetching settings:", error);
			showSnackbar("Error fetching settings", "error");
		} finally {
			setLoading(false);
		}
	};

	const fetchGrades = async () => {
		try {
			const { data, error } = await supabase
				.from("grades")
				.select("parent_grade")
				.not("parent_grade", "is", null)
				.order("parent_grade", { ascending: true });

			if (error) throw error;
			
			// Get distinct parent grades
			const distinctParentGrades = [...new Set(data.map(grade => grade.parent_grade))]
				.map(parentGrade => ({
					id: parentGrade, // Use parent_grade name as id for simplicity
					name: parentGrade,
					parent_grade: null
				}));
			
			// Add "ALL" option at the beginning
			const gradesWithAll = [
				{ id: "ALL", name: "ALL", parent_grade: null },
				...distinctParentGrades
			];
			
			setGrades(gradesWithAll);
		} catch (error) {
			console.error("Error fetching grades:", error);
			showSnackbar("Error fetching grades", "error");
		}
	};

	const showSnackbar = (message, severity = "success") => {
		setSnackbar({ open: true, message, severity });
	};

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	const handleOpenDialog = (setting = null) => {
		if (setting) {
			setEditingSetting(setting);
			setFormData({
				month: setting.month,
				year: setting.year,
				parent_grade: setting.parent_grade || (grades.length > 0 ? grades[0].id : ""),
				number_of_videos_to_be_completed: setting.number_of_videos_to_be_completed,
				description: setting.description || "",
			});
		} else {
			setEditingSetting(null);
			setFormData({
				month: "",
				year: "",
				parent_grade: grades.length > 0 ? grades[0].id : "",
				number_of_videos_to_be_completed: 4,
				description: "",
			});
		}
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
		setEditingSetting(null);
		setFormData({
			month: "",
			year: "",
			parent_grade: grades.length > 0 ? grades[0].id : "",
			number_of_videos_to_be_completed: 4,
			description: "",
		});
	};

	const handleSave = async () => {
		if (!formData.month || !formData.year || !formData.parent_grade || !formData.number_of_videos_to_be_completed) {
			showSnackbar("Please fill in all required fields", "error");
			return;
		}

		setSaving(true);
		try {
			if (editingSetting) {
				// Update existing setting - handle single grade only
				const settingData = {
					month: parseInt(formData.month),
					year: parseInt(formData.year),
					parent_grade: formData.parent_grade,
					number_of_videos_to_be_completed: parseInt(formData.number_of_videos_to_be_completed),
					description: formData.description,
					updated_by: user?.id,
				};

				const { error } = await supabase
					.from("report_settings")
					.update(settingData)
					.eq("id", editingSetting.id);

				if (error) throw error;
				showSnackbar("Setting updated successfully", "success");
			} else {
				// Create new setting(s)
				if (formData.parent_grade === "ALL") {
					// Create settings for all parent grades
					const allParentGrades = grades.filter(grade => grade.id !== "ALL");
					const settingsToCreate = allParentGrades.map(grade => ({
						month: parseInt(formData.month),
						year: parseInt(formData.year),
						parent_grade: grade.id,
						number_of_videos_to_be_completed: parseInt(formData.number_of_videos_to_be_completed),
						description: formData.description,
						created_by: user?.id,
					}));

					const { error } = await supabase
						.from("report_settings")
						.insert(settingsToCreate);

					if (error) throw error;
					showSnackbar(`Settings created successfully for all ${allParentGrades.length} grades`, "success");
				} else {
					// Create single setting
					const settingData = {
						month: parseInt(formData.month),
						year: parseInt(formData.year),
						parent_grade: formData.parent_grade,
						number_of_videos_to_be_completed: parseInt(formData.number_of_videos_to_be_completed),
						description: formData.description,
						created_by: user?.id,
					};

					const { error } = await supabase
						.from("report_settings")
						.insert([settingData]);

					if (error) throw error;
					showSnackbar("Setting created successfully", "success");
				}
			}

			handleCloseDialog();
			fetchSettings();
		} catch (error) {
			console.error("Error saving setting:", error);
			showSnackbar("Error saving setting", "error");
		} finally {
			setSaving(false);
		}
	};


	const getMonthName = (month) => {
		const monthObj = months.find(m => m.value === month);
		return monthObj ? monthObj.label : month;
	};

	const getStatusColor = (setting) => {
		const currentDate = new Date();
		const currentYear = currentDate.getFullYear();
		const currentMonth = currentDate.getMonth() + 1;

		if (setting.year > currentYear || (setting.year === currentYear && setting.month > currentMonth)) {
			return "info"; // Future
		} else if (setting.year === currentYear && setting.month === currentMonth) {
			return "success"; // Current
		} else {
			return "default"; // Past
		}
	};

	const getStatusText = (setting) => {
		const currentDate = new Date();
		const currentYear = currentDate.getFullYear();
		const currentMonth = currentDate.getMonth() + 1;

		if (setting.year > currentYear || (setting.year === currentYear && setting.month > currentMonth)) {
			return "Future";
		} else if (setting.year === currentYear && setting.month === currentMonth) {
			return "Current";
		} else {
			return "Past";
		}
	};

	const getSettingsByYear = () => {
		const byYear = {};
		settings.forEach((setting) => {
			const year = setting.year;
			if (!byYear[year]) {
				byYear[year] = [];
			}
			byYear[year].push(setting);
		});

		return Object.keys(byYear)
			.map((year) => ({
				year: parseInt(year, 10),
				settings: byYear[year].sort((a, b) => {
					if (a.month !== b.month) return a.month - b.month;
					return (a.parent_grade || "").localeCompare(b.parent_grade || "");
				}),
			}))
			.sort((a, b) => b.year - a.year);
	};

	const getMonthlyTotals = () => {
		const monthlyTotals = {};
		
		settings.forEach((setting) => {
			const key = `${setting.year}-${setting.month}`;
			const videos = setting.number_of_videos_to_be_completed || 0;
			
			if (!monthlyTotals[key]) {
				monthlyTotals[key] = {
					month: setting.month,
					year: setting.year,
					totalVideos: videos,
				};
			} else {
				// Keep the higher value
				monthlyTotals[key].totalVideos = Math.max(monthlyTotals[key].totalVideos, videos);
			}
		});

		// Convert to array and sort by year and month
		return Object.values(monthlyTotals).sort((a, b) => {
			if (a.year !== b.year) {
				return a.year - b.year;
			}
			return a.month - b.month;
		});
	};

	return (
		<Container maxWidth="xl" sx={{ py: 4 }}>
			<Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
				Report Settings
			</Typography>
			<Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
				Configure implementation report settings for each month and year.
			</Typography>

			<Grid container spacing={3}>
				{/* Left Column - Form */}
				<Grid item xs={12} md={6}>
					<Card>
						<CardContent>
							<Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
								{editingSetting ? "Edit Setting" : formData.parent_grade === "ALL" ? "Add Settings for All Grades" : "Add New Setting"}
							</Typography>
							<Box component="form" sx={{ mt: 2 }}>
								<Grid container spacing={2}>
									<Grid item xs={6}>
										<FormControl fullWidth size="small">
											<InputLabel>Month</InputLabel>
											<Select
												value={formData.month}
												onChange={(e) => setFormData({ ...formData, month: e.target.value })}
												label="Month"
											>
												{months.map((month) => (
													<MenuItem key={month.value} value={month.value}>
														{month.label}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>
									<Grid item xs={6}>
										<TextField
											fullWidth
											label="Year"
											type="number"
											value={formData.year}
											onChange={(e) => setFormData({ ...formData, year: e.target.value })}
											variant="outlined"
											size="small"
											inputProps={{ min: 2020, max: 2030 }}
										/>
									</Grid>
									<Grid item xs={12}>
										<FormControl fullWidth size="small">
											<InputLabel>Grade</InputLabel>
											<Select
												value={formData.parent_grade}
												onChange={(e) => setFormData({ ...formData, parent_grade: e.target.value })}
												label="Grade"
											>
												{grades.map((grade) => (
													<MenuItem key={grade.id} value={grade.id}>
														{grade.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>
										{formData.parent_grade === "ALL" && (
											<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
												Selecting "ALL" will create settings for all parent grades with the same month, year, and video count.
											</Typography>
										)}
									</Grid>
									<Grid item xs={12}>
										<TextField
											fullWidth
											label="Number of Videos to be Completed"
											type="number"
											value={formData.number_of_videos_to_be_completed}
											onChange={(e) => setFormData({ ...formData, number_of_videos_to_be_completed: e.target.value })}
											variant="outlined"
											size="small"
											inputProps={{ min: 1, max: 100 }}
											helperText="Expected number of implementation videos for 100% score"
										/>
									</Grid>
									<Grid item xs={12}>
										<TextField
											fullWidth
											label="Description (Optional)"
											multiline
											rows={3}
											value={formData.description}
											onChange={(e) => setFormData({ ...formData, description: e.target.value })}
											variant="outlined"
											size="small"
											placeholder="Add any notes or description for this setting..."
										/>
									</Grid>
									<Grid item xs={12}>
										<Stack direction="row" spacing={2}>
											<Button
												variant="contained"
												onClick={handleSave}
												disabled={saving}
												startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
											>
												{saving ? "Saving..." : editingSetting ? "Update" : "Save"}
											</Button>
											<Button
												variant="outlined"
												onClick={handleCloseDialog}
												disabled={saving}
											>
												Cancel
											</Button>
										</Stack>
									</Grid>
								</Grid>
							</Box>
						</CardContent>
					</Card>
				</Grid>

				{/* Right Column - Settings List */}
				<Grid item xs={12} md={6}>
					<Card>
						<CardContent>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
								<Typography variant="h6" sx={{ fontWeight: 600 }}>
									Saved Settings ({settings.length})
								</Typography>
								<Button
									variant="contained"
									startIcon={<AddIcon />}
									onClick={() => handleOpenDialog()}
									size="small"
								>
									Add New
								</Button>
							</Box>

							{loading ? (
								<Box display="flex" justifyContent="center" py={4}>
									<CircularProgress />
								</Box>
							) : settings.length === 0 ? (
								<Box sx={{ py: 4, textAlign: "center" }}>
									<Typography variant="body2" color="text.secondary">
										No settings found. Add your first setting to get started.
									</Typography>
								</Box>
							) : (
								<Stack spacing={1}>
									{getSettingsByYear().map(({ year, settings: yearSettings }) => (
										<Accordion
											key={year}
											disableGutters
											elevation={0}
											sx={{
												border: 1,
												borderColor: "divider",
												borderRadius: 1,
												"&:before": { display: "none" },
												"&.Mui-expanded": { margin: 0 },
											}}
										>
											<AccordionSummary expandIcon={<ExpandMoreIcon />}>
												<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
													{year}
												</Typography>
												<Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
													({yearSettings.length} setting{yearSettings.length !== 1 ? "s" : ""})
												</Typography>
											</AccordionSummary>
											<AccordionDetails sx={{ p: 0 }}>
												<TableContainer>
													<Table size="small">
														<TableHead>
															<TableRow>
																<TableCell>Month</TableCell>
																<TableCell align="center">Grade</TableCell>
																<TableCell align="center">Videos Required</TableCell>
																<TableCell align="center">Status</TableCell>
																<TableCell align="center">Actions</TableCell>
															</TableRow>
														</TableHead>
														<TableBody>
															{yearSettings.map((setting) => (
																<TableRow key={setting.id}>
																	<TableCell>
																		<Typography variant="body2" sx={{ fontWeight: 500 }}>
																			{getMonthName(setting.month)}
																		</Typography>
																		{setting.description && (
																			<Typography variant="caption" color="text.secondary">
																				{setting.description}
																			</Typography>
																		)}
																	</TableCell>
																	<TableCell align="center">
																		<Typography variant="body2" sx={{ fontWeight: 500 }}>
																			{setting.parent_grade || "N/A"}
																		</Typography>
																	</TableCell>
																	<TableCell align="center">
																		<Chip
																			label={setting.number_of_videos_to_be_completed}
																			size="small"
																			color="primary"
																			variant="outlined"
																		/>
																	</TableCell>
																	<TableCell align="center">
																		<Chip
																			label={getStatusText(setting)}
																			size="small"
																			color={getStatusColor(setting)}
																			variant="outlined"
																		/>
																	</TableCell>
																	<TableCell align="center">
																		<IconButton
																			size="small"
																			onClick={() => handleOpenDialog(setting)}
																			color="primary"
																		>
																			<EditIcon fontSize="small" />
																		</IconButton>
																	</TableCell>
																</TableRow>
															))}
														</TableBody>
													</Table>
												</TableContainer>
											</AccordionDetails>
										</Accordion>
									))}
								</Stack>
							)}
						</CardContent>
					</Card>

					{/* Monthly Totals Table */}
					<Card sx={{ mt: 3 }}>
						<CardContent>
							<Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
								Monthly Video Totals
							</Typography>

							{loading ? (
								<Box display="flex" justifyContent="center" py={4}>
									<CircularProgress />
								</Box>
							) : (
								<TableContainer>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>Month/Year</TableCell>
												<TableCell align="center">Total Videos Required</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{getMonthlyTotals().length === 0 ? (
												<TableRow>
													<TableCell colSpan={2} align="center" sx={{ py: 4 }}>
														<Typography variant="body2" color="text.secondary">
															No monthly totals available.
														</Typography>
													</TableCell>
												</TableRow>
											) : (
												<>
													{getMonthlyTotals().map((total, index) => (
														<TableRow key={`${total.year}-${total.month}-${index}`}>
															<TableCell>
																<Typography variant="body2" sx={{ fontWeight: 500 }}>
																	{getMonthName(total.month)} {total.year}
																</Typography>
															</TableCell>
															<TableCell align="center">
																<Chip
																	label={total.totalVideos}
																	size="small"
																	color="secondary"
																	variant="outlined"
																/>
															</TableCell>
														</TableRow>
													))}
													<TableRow sx={{ backgroundColor: 'action.hover' }}>
														<TableCell>
															<Typography variant="body2" sx={{ fontWeight: 600 }}>
																Total
															</Typography>
														</TableCell>
														<TableCell align="center">
															<Chip
																label={getMonthlyTotals().reduce((sum, total) => sum + total.totalVideos, 0)}
																size="small"
																color="secondary"
																variant="filled"
															/>
														</TableCell>
													</TableRow>
												</>
											)}
										</TableBody>
									</Table>
								</TableContainer>
							)}
						</CardContent>
					</Card>
				</Grid>
			</Grid>

			{/* Snackbar for notifications */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			>
				<Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default ReportSettings;
